import MobileServiceClient from "@mocobaas/client-js/build/MobileServiceClient";
import {
  CreateParams,
  CreateResult,
  GetListParams,
  GetListResult,
  GetManyParams,
  GetManyResult,
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
  DeleteParams,
  DeleteResult,
} from "react-admin";
import gql from "graphql-tag";
import { ProviderBase } from "./ProviderBase";
import { buildArgs } from "./utils";
import moment from "moment";
import { LexoRank } from "lexorank";
import mbaas from "../provider/Mbaas";

export class PromotionsPopupProviderClass extends ProviderBase<any> {
  client: MobileServiceClient;

  constructor(client: MobileServiceClient) {
    super();
    this.client = client;
  }

  async getList(
    resource: string,
    params: GetListParams
  ): Promise<GetListResult> {
    const where: Record<string, any> = {};
    const filterArray: any[] = [];
    const dateNow = Date.now();

    if (params.filter.type === "ACTIVE") {
      filterArray.push({
        date_start: {
          _lte: moment(dateNow).toISOString(),
        },
      });
      filterArray.push({
        date_end: {
          _gte: moment(dateNow).toISOString(),
        },
      });
    } else if (params.filter.type === "INACTIVE") {
      filterArray.push({
        date_start: {
          _gte: moment(dateNow).toISOString(),
        },
      });
    } else {
      filterArray.push({
        date_end: {
          _lte: moment(dateNow).toISOString(),
        },
      });
    }

    if (params.filter.country) {
      filterArray.push({
        country: {
          _eq: params.filter.country,
        },
      });
    }
    if (params.filter.account_type) {
      filterArray.push({
        account_type: {
          _eq: params.filter.account_type,
        },
      });
    }

    filterArray.push({ type: { _eq: "POPUP" } });

    if (filterArray.length > 0) where._and = filterArray;

    const { page, perPage } = params.pagination;
    let { field, order } = params.sort;
    const args = buildArgs({
      // @ts-ignore
      size: perPage,
      pageNumber: page,
      sort: "priority",
      order: "ASC",
    });
    const queryResult = await this.client.gql.query({
      query: gql`
        query PromotionsPopupList($where: PromotionalBannersWhereInput) {
          allPromotionalBannersList(where: $where, ${args}) {
            data {
              id
              created_at
              updated_at
              title
              image
              url
              country
              date_start
              date_end
              account_type
              author_id
              priority
            }
            count
          }
        }
      `,
      variables: {
        where,
      },
    });
    return {
      data: queryResult?.allPromotionalBannersList?.data,
      total: queryResult?.allPromotionalBannersList?.count,
    };
  }

  async create(
    resource: string,
    params: CreateParams<any>
  ): Promise<CreateResult<any>> {
    const rawImage = params?.data?.urlThumbnail;

    let urlImg: string[];
    try {
      const response: any = await this.client.storage.upload({
        file: rawImage.rawFile,
        params: {
          filename: `popupPromo-${Date.now()}`,
          bucket: "banners",
        },
      });

      const download = await this.client.storage.downloadUrl({
        params: {
          bucket: "banners",
        },
        fileId: response?.data.id,
      });

      urlImg = download.data.url.split("?");
    } catch (error) {
      console.error("Error uploading image:", error);
      throw new Error("Failed to upload image");
    }

    const { data } = await this.client.user.get();

    const newKey = LexoRank.middle().toString();

    const submittedData = {
      type: "POPUP",
      country: params?.data?.country_code,
      image: urlImg[0],
      title: params?.data?.title,
      url:
        params?.data?.url === "custom_url"
          ? params?.data?.custom_url
          : params?.data?.url,
      account_type: params?.data?.account_type,
      date_start: params?.data?.date_start,
      date_end: params?.data?.date_end,
      author_id: data?.id,
      priority: newKey,
    };

    const queryResult = await this.client.gql.mutation({
      mutation: gql`
        mutation ($input: [PromotionalBannersCreateInput!]!) {
          createPromotionalBanners(input: $input) {
            id
            title
            image
            url
            country
            date_start
            date_end
            account_type
            author_id
          }
        }
      `,
      variables: {
        input: submittedData,
      },
    });

    return { data: queryResult?.createPromotionalBanners[0] } as CreateResult;
  }

  async update(
    resource: string,
    params: UpdateParams<any>
  ): Promise<UpdateResult<any>> {
    const { data } = await this.client.user.get();

    let inputData = {
      type: "POPUP",
      country: params?.data?.country,
      title: params?.data?.title,
      url:
        params?.data?.url === "custom_url"
          ? params?.data?.custom_url
          : params?.data?.url,
      date_start: params?.data?.date_start,
      account_type: params?.data?.account_type,
      date_end: params?.data?.date_end,
      author_id: data?.id,
    };

    if (params.data.urlThumbnail) {
      const rawImage = params?.data?.urlThumbnail;
      try {
        const response: any = await this.client.storage.upload({
          file: rawImage.rawFile,
          params: {
            filename: `popupPromo-${Date.now()}-${params?.data?.priority}`,
            bucket: "banners",
          },
        });

        const download = await this.client.storage.downloadUrl({
          params: {
            bucket: "banners",
          },
          fileId: response?.data.id,
        });

        const urlImg = download.data.url.split("?");

        inputData = {
          ...inputData,
          image: urlImg[0],
        } as any;
      } catch (error) {
        console.error("Error uploading image:", error);
        throw new Error("Failed to upload image");
      }
    }

    const { updatePromotionalBannersById, error } =
      await this.client.gql.mutation({
        mutation: `
      mutation updatePromoPopup($ids: [UUID!]!, $input: PromotionalBannersUpdateInput!){
        updatePromotionalBannersById(ids: $ids, input: $input){
            id
            title
            image
            url
            country
            date_start
            date_end
            account_type
            author_id
          }
        }
      `,
        variables: {
          ids: params.data.id,
          input: inputData,
        },
      });

    if (error) {
      console.warn(error);
      throw new Error("GraphQL query error");
    }

    return { data: updatePromotionalBannersById[0] } as UpdateResult;
  }

  async getOne(
    resource: string,
    params: GetOneParams<any>
  ): Promise<GetOneResult<any>> {
    const queryResult = await this.client.gql.query({
      query: gql`
        query getOnePromoPopup($id: UUID!) {
          getPromotionalBannersById(id: $id) {
            id
            created_at
            updated_at
            title
            image
            url
            country
            date_start
            date_end
            account_type
            author_id
            author_id_data {
              id
              email
              name
            }
          }
        }
      `,
      variables: {
        id: params.id,
      },
    });

    const resp = await mbaas.client.invokeApi("console-webview-link-list", {
      country: queryResult?.getPromotionalBannersById?.country,
      platform: [queryResult?.getPromotionalBannersById?.account_type],
    });

    const linkList = resp.error ? [] : resp.data ?? [];
    linkList.push({ url: "custom_url", label: "CUSTOM URL" });

    const ifValueExist = (value: any) => {
      return linkList.some((el: any) => {
        return el.url === value;
      });
    };

    let custom_url;

    if (!ifValueExist(queryResult?.getPromotionalBannersById?.url)) {
      custom_url = queryResult?.getPromotionalBannersById?.url;

      if (queryResult?.getPromotionalBannersById?.url !== undefined) {
        // @ts-ignore
        queryResult.getPromotionalBannersById["url"] = "custom_url";
      }
    }

    return {
      data: { ...queryResult?.getPromotionalBannersById, custom_url },
    } as GetOneResult;
  }

  async delete(
    resource: string,
    params: DeleteParams<any>
  ): Promise<DeleteResult<any>> {
    const queryResult = await this.client.gql.mutation({
      mutation: gql`
        mutation ($ids: [UUID!]!) {
          deletePromotionalBannersById(ids: $ids)
        }
      `,
      variables: {
        ids: [`${params.id}`],
      },
    });

    return {
      data: queryResult?.deletePromotionalBannersById[0],
    } as DeleteResult;
  }

  async getMany(
    resource: string,
    params: GetManyParams
  ): Promise<GetManyResult<any>> {
    return { data: [] };
  }

  async getManyReference(
    resource: string,
    params: GetManyReferenceParams
  ): Promise<GetManyReferenceResult<any>> {
    return { data: [], total: 0 };
  }
}
